import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AcceptInvite from './pages/AcceptInvite';
import PublicForm from './PublicForm';
import './App.css';

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const decoded = JSON.parse(atob(parts[1]));
    if (!decoded.exp) return false;
    return decoded.exp * 1000 < Date.now();
  } catch (e) {
    return false;
  }
}

function Root() {
  const publicFormMatch = window.location.pathname.match(/^\/forms\/([^/]+)\/?$/);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState('login');
  const [activeProject, setActiveProject] = useState(null);
  const [userRole, setUserRole] = useState('admin');
  const [inviteToken, setInviteToken] = useState(null);

  const resetTokenMatch = window.location.pathname.match(/^\/reset-password\/(.+)$/);
  const urlParams = new URLSearchParams(window.location.search);
  const queryResetToken = urlParams.get('resetToken') || urlParams.get('token');
  const initialResetToken = (resetTokenMatch ? decodeURIComponent(resetTokenMatch[1]) : null) || queryResetToken;

  useEffect(() => {
    const path = window.location.pathname;
    const inviteMatch = path.match(/^\/invite\/(.+)$/);
    if (inviteMatch) setInviteToken(inviteMatch[1]);

    const token = localStorage.getItem('cms_token');
    const savedUser = localStorage.getItem('cms_user');
    // If a reset token is present in the URL, force the login screen to allow resetting password
    if (initialResetToken) {
      setScreen('login');
      setLoading(false);
      return;
    }
    if (token && savedUser && !isTokenExpired(token)) {
      const u = JSON.parse(savedUser);
      setUser(u);
      setScreen(inviteMatch ? 'invite' : 'dashboard');
    } else {
      if (inviteMatch) setScreen('invite');
    }
    setLoading(false);
  }, [initialResetToken]);

  useEffect(() => {
    if (!user) return;

    const checkTokenExpiration = () => {
      const token = localStorage.getItem('cms_token');
      if (isTokenExpired(token)) {
        handleLogout();
      }
    };

    const interval = setInterval(checkTokenExpiration, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogin = (u) => {
    setUser(u);
    setScreen(inviteToken ? 'invite' : 'dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('cms_token');
    localStorage.removeItem('cms_user');
    setUser(null);
    setScreen('login');
    setActiveProject(null);
    window.history.pushState({}, '', '/');
  };

  const handleOpenProject = (project, role) => {
    setActiveProject(project);
    setUserRole(role);
    setScreen('cms');
  };

  if (publicFormMatch) {
    return <PublicForm slug={decodeURIComponent(publicFormMatch[1])} />;
  }

  if (loading) return (
    <div style={{minHeight:'100vh',background:'#0f0f0f',display:'flex',alignItems:'center',justifyContent:'center',color:'#666',fontFamily:'IBM Plex Sans, sans-serif'}}>
      Loading...
    </div>
  );

  if (screen === 'invite') return (
    <AcceptInvite token={inviteToken} user={user}
      onDone={() => { window.history.pushState({}, '', '/'); setScreen(user ? 'dashboard' : 'login'); }} />
  );

  if (!user || screen === 'login') return <Login onLogin={handleLogin} resetToken={initialResetToken} />;

  if (screen === 'dashboard') return (
    <Dashboard user={user} onOpenProject={handleOpenProject} onLogout={handleLogout} />
  );

  if (screen === 'cms') return (
    <App user={user} userRole={userRole} project={activeProject}
      onBack={() => { setScreen('dashboard'); setActiveProject(null); }} />
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<Root />);
